import React, { useState, useEffect, useRef } from "react";
import wordlist_de from "../data/wordlist.de.json";

const flattenWords = (obj) =>
  Object.entries(obj).flatMap(([category, arr]) => arr.map(word => ({ translated: word, category })));

const DIFFICULTY_CATEGORIES = {
  easy: ["core_words", "family", "colors", "animals", "food_and_drink"],
  medium: ["home", "school", "toys", "clothing"],
  hard: ["tools", "transport", "technology", "nature"],
};

function filterWordsByDifficulty(words, difficulty) {
  return words.filter(wordObj => {
    const word = wordObj.translated || wordObj;
    const cat = wordObj.category || "other";
    const len = word.length;
    if (difficulty === "easy") {
      return len <= 4 && DIFFICULTY_CATEGORIES.easy.includes(cat);
    }
    if (difficulty === "medium") {
      return len >= 5 && len <= 7 && DIFFICULTY_CATEGORIES.medium.includes(cat);
    }
    if (difficulty === "hard") {
      return len >= 8 && DIFFICULTY_CATEGORIES.hard.includes(cat);
    }
    return true;
  });
}

const TimedReading = ({ words, language, labels, onScore, category, onViewResults }) => {
  const [index, setIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [isRunning, setIsRunning] = useState(false);
  const [wordsRead, setWordsRead] = useState(0);
  const [finished, setFinished] = useState(false);
  const [difficulty, setDifficulty] = useState(null);
  const wordPoolRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!isRunning) return;

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setFinished(true);
          setIsRunning(false);
          if (onScore) onScore(wordsRead);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [isRunning]);

  const startGame = (selectedDifficulty) => {
    if (timerRef.current) clearInterval(timerRef.current);
    let wordPool;
    if (Array.isArray(words) && words.length && typeof words[0] === 'object') {
      wordPool = words;
    } else if (language === "de") {
      wordPool = flattenWords(wordlist_de);
    } else {
      wordPool = flattenWords(words);
    }
    // Filter by category if provided
    if (category) {
      wordPool = wordPool.filter(w => w.category === category);
    }
    if (selectedDifficulty) {
      wordPool = filterWordsByDifficulty(wordPool, selectedDifficulty);
    }
    wordPoolRef.current = wordPool;
    setIsRunning(true);
    setFinished(false);
    setWordsRead(0);
    setTimeLeft(30);
    setIndex(Math.floor(Math.random() * wordPoolRef.current.length));
    setDifficulty(selectedDifficulty);
  };

  const nextWord = () => {
    if (!wordPoolRef.current.length) return;
    setIndex(Math.floor(Math.random() * wordPoolRef.current.length));
    setWordsRead((prev) => prev + 1);
  };

  // Compute available difficulties for the current category
  const getAvailableDifficulties = () => {
    let wordPool;
    if (Array.isArray(words) && words.length && typeof words[0] === 'object') {
      wordPool = words;
    } else if (language === "de") {
      wordPool = flattenWords(wordlist_de);
    } else {
      wordPool = flattenWords(words);
    }
    if (category) {
      wordPool = wordPool.filter(w => w.category === category);
    }
    const difficulties = ['easy', 'medium', 'hard'];
    const available = {};
    difficulties.forEach(diff => {
      const filtered = filterWordsByDifficulty(wordPool, diff);
      available[diff] = filtered.length >= 10;
    });
    return available;
  };

  const availableDifficulties = getAvailableDifficulties();
  const allUnavailable = !availableDifficulties.easy && !availableDifficulties.medium && !availableDifficulties.hard;
  const someUnavailable = Object.values(availableDifficulties).some(v => !v);

  // Difficulty selection UI
  if (!isRunning && !finished && !difficulty) {
    return (
      <div style={{ width: '100%', maxWidth: 420, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <label style={{ fontSize: '1.3em', marginBottom: 12, textAlign: 'center' }}>{labels.chooseDifficulty || "Choose difficulty:"}</label>
        {availableDifficulties.easy && (
          <button className="green-button" style={{ fontSize: '1.3rem', padding: '18px 0', width: '100%' }} onClick={() => setDifficulty('easy')}>{labels.difficultyEasy || 'Easy'}</button>
        )}
        {availableDifficulties.medium && (
          <button className="green-button" style={{ fontSize: '1.3rem', padding: '18px 0', width: '100%' }} onClick={() => setDifficulty('medium')}>{labels.difficultyMedium || 'Medium'}</button>
        )}
        {availableDifficulties.hard && (
          <button className="green-button" style={{ fontSize: '1.3rem', padding: '18px 0', width: '100%' }} onClick={() => setDifficulty('hard')}>{labels.difficultyHard || 'Hard'}</button>
        )}
        {someUnavailable && !allUnavailable && (
          <div style={{ color: '#f44336', marginTop: 8, textAlign: 'center', fontSize: '1.1em' }}>
            {labels.notEnoughWords || "We don't have yet enough words for the other difficulties."}
          </div>
        )}
        {allUnavailable && (
          <div style={{ color: '#f44336', marginTop: 8, textAlign: 'center', fontSize: '1.1em' }}>
            {labels.noWords || "No words available for this category."}
          </div>
        )}
      </div>
    );
  }

  // After difficulty is chosen, show Start button
  if (!isRunning && !finished && difficulty) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 16, width: '100%' }}>
        <button onClick={() => startGame(difficulty)} className="green-button" style={{ fontSize: '1.3rem', padding: '18px 36px', width: '100%' }}>{labels.start || "Start"}</button>
        <button onClick={() => setDifficulty(null)} className="small-button" style={{ width: '100%' }}>{labels.backToGame || 'Back to Game'}</button>
      </div>
    );
  }

  // Guard: if wordPoolRef.current is empty after filtering, show a message
  if ((isRunning || finished) && (!wordPoolRef.current || wordPoolRef.current.length === 0)) {
    return (
      <div className="timed-reading" style={{ textAlign: 'center', padding: 32 }}>
        <h2>{labels.noWords || "No words available for this category and difficulty."}</h2>
        <button onClick={() => { setDifficulty(null); setFinished(false); setIsRunning(false); setWordsRead(0); setTimeLeft(30); }} className="green-button" style={{ marginTop: 24 }}>
          {labels.backToGame || 'Back to Game'}
        </button>
      </div>
    );
  }

  return (
    <div className="timed-reading">
      <h2>{labels.timed}</h2>
      {isRunning && (
        <>
          <h3 className="timed-word" style={{ fontSize: '2.5em', fontWeight: 700 }}>
            {wordPoolRef.current[index]?.translated}
          </h3>
          <button onClick={nextWord}>{labels.next}</button>
          <p>{labels.remainingTime || "Remaining time"}: {timeLeft}s</p>
          <p>{labels.wordsRead || "Words read"}: {wordsRead}</p>
        </>
      )}
      {finished && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
          <p>
            {(labels.finished || "Finished!")} {labels.correctRead}: {wordsRead}
          </p>
          <button onClick={() => { setDifficulty(null); setFinished(false); setWordsRead(0); setTimeLeft(30); }} className="green-button" style={{ marginTop: 24 }}>
            {labels.newGame || "New Game"}
          </button>
          <button onClick={onViewResults} className="small-button" style={{ marginTop: 16 }}>
            {labels.viewResults || "View Results"}
          </button>
        </div>
      )}
    </div>
  );
};

export default TimedReading;